import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import LoginService from "../../Services/login_service";
import ProductService from "../../Services/products_service";
import {
  FaEnvelope,
  FaPhoneAlt,
  FaWhatsapp,
  FaSearch,
  FaBars,
  FaTimes,
  FaUserCircle,
} from "react-icons/fa";
import "./Navbar.css";
import Varanouslabslogo from "../../assets/Varanouslabslogo.png";
import ServicesDropdown from "../Services/ServiceDropDown/ServiceDropDown";

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const [serviceDropdownOpen, setServiceDropdownOpen] = useState(false);
  const [supportDropdownOpen, setSupportDropdownOpen] = useState(false);
  const [termsDropdownOpen, setTermsDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isLaptopMode, setIsLaptopMode] = useState(
    window.innerWidth > 768 && window.innerWidth <= 1280
  );
  const [isSystemDesktopMode, setIsSystemDesktopMode] = useState(
    window.innerWidth > 1280
  );
  const [showProfile, setShowProfile] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [suggestedProducts, setSuggestedProducts] = useState([]);
  const [isSuggestionVisible, setIsSuggestionVisible] = useState(false);
  const [isServiceHovered, setIsServiceHovered] = useState(false);
  const menuRef = useRef(null);
  const profileRef = useRef(null);
  const navigate = useNavigate();

  const [user, setUser] = useState(null);

useEffect(() => {
  const currentUser = LoginService.getCurrentUser();
  setUser(currentUser || null);
}, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsLaptopMode(window.innerWidth > 768 && window.innerWidth <= 1280);
      setIsSystemDesktopMode(window.innerWidth > 1280);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchProductCount = async () => {
      const result = await ProductService.getProductCount();
      if (result.success) {
        console.log("Product count:", result.count);
      } else {
        console.error("Failed to fetch product count:", result.message);
      }
    };
    fetchProductCount();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
        closeAllDropdowns();
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfile(false);
      }
      if (
        !event.target.closest(".search-input-button") &&
        !event.target.className.includes("search-btn")
      ) {
        setIsSuggestionVisible(false);
      }
    };

    if (menuOpen || showProfile || isSuggestionVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen, showProfile, isSuggestionVisible]);

  const closeAllDropdowns = () => {
    setAboutDropdownOpen(false);
    setServiceDropdownOpen(false);
    setSupportDropdownOpen(false);
    setTermsDropdownOpen(false);
    setIsServiceHovered(false);
  };

  const handleSearch = async () => {
    if (!searchText.trim()) return;

    const query = searchText.trim();
    const lowerQuery = query.toLowerCase();

    // Catalog No (e.g., VL-B107002)
    if (/^VL-\w+$/i.test(query)) {
      try {
        const result = await ProductService.fetchProductsBySlug(query);
        if (result.success) {
          const categorySlug = result.data.categorySlug || "unknown";
          navigate(`/product-detail/${query}`, { state: { categorySlug } });
          setSearchText("");
          setIsSuggestionVisible(false);
          return;
        } else {
          alert(result.message || "Product not found by Catalog No");
        }
      } catch (error) {
        console.error("Error fetching product by Catalog No:", error);
        alert("Error occurred while fetching Catalog No");
      }
      return;
    }

    // CAS No pattern (e.g., 875-74-1)
    const casRegex = /^\d{2,7}-\d{2}-\d{1}$/;
    if (casRegex.test(query)) {
      try {
        const result = await ProductService.fetchProductByCasNo(query);
        if (result.success && result.data) {
          const { catalogNumber, categorySlug = "unknown" } = result.data;
          if (catalogNumber) {
            navigate(`/product-detail/${catalogNumber}`, {
              state: { categorySlug },
            });
            setSearchText("");
            setIsSuggestionVisible(false);
          } else {
            alert("Product found but catalog number is missing");
          }
        } else {
          alert(result.message || `No product found for CAS No: ${query}`);
        }
      } catch (error) {
        console.error("Error fetching product by CAS No:", error);
        alert("Error occurred while fetching CAS No");
      }
      return;
    }

    // Fallback to category product search
    try {
      const firstChar = query.charAt(0);
      const alphaKey = /^[0-9]/.test(firstChar)
        ? "0-9"
        : firstChar.toLowerCase();

      const categoryProducts = await ProductService.getCategoriesByAlpabet(
        alphaKey
      );

      if (categoryProducts.length > 0) {
        const matchedProduct = categoryProducts.find(
          (product) => product.name?.toLowerCase() === lowerQuery
        );

        if (matchedProduct && matchedProduct.catalogNumber) {
          const { catalogNumber, name, categorySlug } = matchedProduct;
          setSuggestedProducts([
            { name, catalogNumber, categorySlug: categorySlug || alphaKey },
          ]);
          setIsSuggestionVisible(true);
          return;
        }

        navigate(
          `/products?type=category&query=${encodeURIComponent(lowerQuery)}`
        );
        setSearchText("");
        setIsSuggestionVisible(false);
      } else {
        alert(`No products found for query: "${query}"`);
        setIsSuggestionVisible(false);
      }
    } catch (error) {
      console.error("Search error:", error);
      alert("Something went wrong while searching");
      setIsSuggestionVisible(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const toggleMenu = (e) => {
    e.stopPropagation();
    const newState = !menuOpen;
    setMenuOpen(newState);
    if (!newState) closeAllDropdowns();
  };

  const handleLinkClick = () => {
    setMenuOpen(false);
    closeAllDropdowns();
  };

  const toggleDropdown =
    (setter, ...others) =>
    (e) => {
      e.stopPropagation();
      setter((prev) => {
        if (!prev) others.forEach((fn) => fn(false));
        return !prev;
      });
    };

const handleLogout = () => {
  localStorage.removeItem("user");
  localStorage.removeItem("accessToken");

  setUser(null);          // 🔥 TRIGGERS RE-RENDER
  setShowProfile(false);
  setMenuOpen(false);     // optional but recommended
  navigate("/");
};

  const closeDropdown = () => {
    setIsServiceHovered(false);
    setServiceDropdownOpen(false);
    handleLinkClick();
  };

  const handleSuggestionClick = (catalogNumber, categorySlug) => {
    if (catalogNumber) {
      navigate(`/product-detail/${catalogNumber}`, {
        state: { categorySlug },
      });
      setSearchText("");
      setIsSuggestionVisible(false);
    }
  };

  return (
    <header className="header-container">
      <div
        className="top-bar1"
        style={{ maxWidth: "100%", boxSizing: "border-box" }}
      >
        <div className="left-info">
          <a href="mailto:bd@varanouslabs.com" className="contact-link">
            <FaEnvelope style={{ marginRight: "6px", color: "#DA7D31FF" }} />
            bd@varanouslabs.com
          </a>
          <a href="tel:9063488659" className="contact-link">
            <FaPhoneAlt style={{ marginRight: "6px", color: "#EE2314FF" }} />
            9063488659
          </a>
         <a
  href="https://wa.me/9063488659?text=Hi%20Varanous%20Labs%20team,%20I%20would%20like%20to%20know%20more%20about%20your%20services."
  target="_blank"
  rel="noopener noreferrer"
  style={{
    color: "#F5F7FA",
    textDecoration: "none",
    display: "flex",
    alignItems: "center",
    gap: "5px", // space between icon and number
  }}
>
  <FaWhatsapp style={{ color: "#25D366" }} />
  9063488659
</a>

        </div>

        {!user && (
          <div className="auth-links">
            <Link to="/login" className="login-link" onClick={handleLinkClick}>
              Login
            </Link>
          </div>
        )}

        {user && (
          <div className="auth-links">
            <Link
              to="/customerPortal/myDashBoard"
              className="register-link"
              onClick={handleLinkClick}
            >
              My DashBoard
            </Link>
            <section className="user-profile-section" ref={profileRef}>
              <button
                className="profile-toggle-button"
                onClick={() => setShowProfile((prev) => !prev)}
              >
                <FaUserCircle
                  size={isLaptopMode ? 20 : 22}
                  style={{ marginRight: "6px" }}
                />
                {user.firstName}
              </button>

             {showProfile && (
          <div className="user-profile-details space-y-2 p-4 absolute right-4 top-14 bg-white text-black shadow-lg rounded border z-10">
            {[
              { label: "First Name", value: user.firstName },
              { label: "Last Name", value: user.lastName },
              { label: "Email", value: user.email },
              { label: "Mobile", value: user.mobile },
              { label: "Company", value: user.company },
            ].map(
              (item, index) =>
                item.value && (
                  <div key={index} className="flex">
                    <span className="w-[100px] font-semibold">
                      {item.label}
                    </span>
                    <span className="w-4 text-right">:</span>
                    <span className="flex-1 pl-2">{item.value}</span>
                  </div>
                )
            )}
            <div className="flex justify-around">
              <button
                onClick={() => setShowProfile(false)}
                className="mt-4 px-4 py-0 bg-black text-white rounded hover:bg-black"
              >
                Close
              </button>
             <button
  onClick={(e) => {
    e.stopPropagation();
    handleLogout();
  }}
  className="mt-4 px-4 py-0 bg-red-600 text-white rounded hover:bg-red-700"
>
  Logout
</button>

            </div>
          </div>
        )}

            </section>
          </div>
        )}
      </div>

      <div
        className="middle-bar"
        style={{ maxWidth: "100%", boxSizing: "border-box" }}
      >
        <Link
          to="/"
          onClick={handleLinkClick}
          className="flex justify-center md:justify-start mt-8 md:mt-2"
        >
          <img
            src={Varanouslabslogo}
            alt="Varanous Logo"
            className="logoHeader pb-1 w-[100%] md:w-[100%]"
          />
        </Link>

        {/* Navigation menu moved to right side of logo for mobile */}
        {isMobile && (
          <nav className="mobile-nav">
            <button className="menu-btn" onClick={toggleMenu}>
              {menuOpen ? (
                <FaTimes size={isLaptopMode ? 30 : 35} />
              ) : (
                <FaBars size={isLaptopMode ? 30 : 35} />
              )}
            </button>
          </nav>
        )}

        <div className="search-container">
          {/* Full-width search bar for mobile */}
          <div
  className="search-input-button"
  style={{ width: isMobile ? "100%" : "auto" }}
>

            <input
              type="text"
              placeholder="Catalog No, CAS No, or Category Name"
              className="search-input"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setIsSuggestionVisible(false);
                setSuggestedProducts([]);
              }}
              onKeyDown={handleKeyDown}
            />
            <button className="search-btn" onClick={handleSearch}>
              <FaSearch />
            </button>
            {isSuggestionVisible && suggestedProducts.length > 0 && (
              <div
                style={{
                  backgroundColor: "#f8e7f8",
                  padding: "8px",
                  borderRadius: "4px",
                  marginTop: "4px",
                  color: "#333",
                }}
              >
                {suggestedProducts.map((product, index) => (
                  <div
                    key={index}
                    style={{ cursor: "pointer", padding: "4px" }}
                    onClick={() =>
                      handleSuggestionClick(
                        product.catalogNumber,
                        product.categorySlug
                      )
                    }
                  >
                    {product.name}
                  </div>
                ))}
              </div>
            )}
          </div>
          {!isMobile && (
            <button className="menu-btn" onClick={toggleMenu}>
              {menuOpen ? (
                <FaTimes size={isLaptopMode ? 30 : 35} />
              ) : (
                <FaBars size={isLaptopMode ? 30 : 35} />
              )}
            </button>
          )}
        </div>
      </div>

      <nav
        className={`nav-bar ${isMobile || isLaptopMode ? "hidden" : "flex"}`}
        style={{ maxWidth: "100%", boxSizing: "border-box" }}
      >
        <ul className="nav-list" style={{ width: "100%" }}>
          <li>
            <Link to="/" className="text-cen" onClick={handleLinkClick}>
              HOME
            </Link>
          </li>
          <li
            className="dropdown"
            onMouseEnter={() =>
              isSystemDesktopMode && setAboutDropdownOpen(true)
            }
            onMouseLeave={() =>
              isSystemDesktopMode && setAboutDropdownOpen(false)
            }
          >
            <span
              className="dropdown-title"
              onClick={toggleDropdown(
                setAboutDropdownOpen,
                setServiceDropdownOpen,
                setSupportDropdownOpen,
                setTermsDropdownOpen
              )}
            >
              ABOUT US {aboutDropdownOpen ? "▲" : "▼"}
            </span>
            {aboutDropdownOpen && (
              <div className="dropdown-menu">
                <ul>
                  <li>
                    <Link to="/about-varanous" onClick={handleLinkClick}>
                      About Varanous
                    </Link>
                  </li>
                  <li>
                    <Link to="/varanous-life" onClick={handleLinkClick}>
                      Varanous Life
                    </Link>
                  </li>
                  <li>
                    <Link to="/careers" onClick={handleLinkClick}>
                      Careers
                    </Link>
                  </li>
                </ul>
              </div>
            )}
          </li>
          <li>
            <Link to="/Products" className="text-cen" onClick={handleLinkClick}>
              PRODUCTS
            </Link>
          </li>
          <li
            className="nav-item dropdown-wrapper"
            onMouseEnter={() =>
              isSystemDesktopMode && setIsServiceHovered(true)
            }
            onMouseLeave={() =>
              isSystemDesktopMode && setIsServiceHovered(false)
            }
          >
            <span
              className="nav-link dropdown-label service_color"
              onClick={toggleDropdown(
                setServiceDropdownOpen,
                setAboutDropdownOpen,
                setSupportDropdownOpen,
                setTermsDropdownOpen
              )}
            >
              SERVICES {isServiceHovered || serviceDropdownOpen ? "▲" : "▼"}
            </span>
            {(isServiceHovered || serviceDropdownOpen) && (
              <div className="services-dropdown-wrapper">
                <ServicesDropdown closeDropdown={closeDropdown} />
              </div>
            )}
          </li>
          <li>
            <Link to="/facility" className="text-cen" onClick={handleLinkClick}>
              Facilities
            </Link>
          </li>
          <li
            className="dropdown"
            onMouseEnter={() =>
              isSystemDesktopMode && setSupportDropdownOpen(true)
            }
            onMouseLeave={() =>
              isSystemDesktopMode && setSupportDropdownOpen(false)
            }
          >
            <span
              className="dropdown-title"
              onClick={toggleDropdown(
                setSupportDropdownOpen,
                setAboutDropdownOpen,
                setServiceDropdownOpen,
                setTermsDropdownOpen
              )}
            >
              SUPPORT {supportDropdownOpen ? "▲" : "▼"}
            </span>
            {supportDropdownOpen && (
              <div className="dropdown-menu">
                <ul>
                  {/* <li>
                    <Link to="/Catalogue" onClick={handleLinkClick}>
                      Catalogue
                    </Link>
                  </li> */}
                  <li>
                    <Link to="/faqs" onClick={handleLinkClick}>
                      FAQs
                    </Link>
                  </li>
                  <li>
                    <Link to="/contact" onClick={handleLinkClick}>
                      Contact
                    </Link>
                  </li>
                </ul>
              </div>
            )}
          </li>
          <li
            className="dropdown"
            onMouseEnter={() =>
              isSystemDesktopMode && setTermsDropdownOpen(true)
            }
            onMouseLeave={() =>
              isSystemDesktopMode && setTermsDropdownOpen(false)
            }
          >
            <span
              className="dropdown-title"
              onClick={toggleDropdown(
                setTermsDropdownOpen,
                setAboutDropdownOpen,
                setServiceDropdownOpen,
                setSupportDropdownOpen
              )}
            >
              TERMS OF USE {termsDropdownOpen ? "▲" : "▼"}
            </span>
            {termsDropdownOpen && (
              <ul className="dropdown-menu">
                <li>
                  <Link to="/terms-conditions" onClick={handleLinkClick}>
                    Terms & Conditions
                  </Link>
                </li>
              </ul>
            )}
          </li>
          <li>
            <Link
              to="/get-quote"
              className="text-cen"
              onClick={handleLinkClick}
            >
              GET QUOTE
            </Link>
          </li>
          <li>
            <Link to="/help" className="text-cen" onClick={handleLinkClick}>
              HELP
            </Link>
          </li>
        </ul>
      </nav>

      <div
        ref={menuRef}
        className={`mobile-menu ${menuOpen ? "open" : ""}`}
        style={{ maxWidth: "100%", boxSizing: "border-box" }}
      >
        {/* <button className="close-button" onClick={toggleMenu}>
          <FaTimes size={isLaptopMode ? 30 : 35} />
        </button> */}
        <ul className="mobile-nav-list">
          <li>
            <Link to="/" onClick={handleLinkClick}>
              HOME
            </Link>
          </li>
          <li>
            <span
              onClick={toggleDropdown(
                setAboutDropdownOpen,
                setServiceDropdownOpen,
                setSupportDropdownOpen,
                setTermsDropdownOpen
              )}
            >
              ABOUT US
            </span>
            {aboutDropdownOpen && (
              <div className="dropdown-menu12">
                <ul>
                  <li>
                    <button
                      className="dropdown-btn"
                      onClick={() => {
                        navigate("/about-varanous");
                        handleLinkClick();
                      }}
                    >
                      About Varanous
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-btn"
                      onClick={() => {
                        navigate("/varanous-life");
                        handleLinkClick();
                      }}
                    >
                      Varanous Life
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-btn"
                      onClick={() => {
                        navigate("/careers");
                        handleLinkClick();
                      }}
                    >
                      Careers
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </li>
          <li>
            <Link to="/Products" onClick={handleLinkClick}>
              PRODUCTS
            </Link>
          </li>
          <li>
            <span
              onClick={toggleDropdown(
                setServiceDropdownOpen,
                setAboutDropdownOpen,
                setSupportDropdownOpen,
                setTermsDropdownOpen
              )}
            >
              SERVICES
            </span>
            {serviceDropdownOpen && (
              <div className="dropdown-menu">
                <ServicesDropdown closeDropdown={closeDropdown} />
              </div>
            )}
          </li>
          <li>
            <span
              onClick={toggleDropdown(
                setSupportDropdownOpen,
                setAboutDropdownOpen,
                setServiceDropdownOpen,
                setTermsDropdownOpen
              )}
            >
              SUPPORT
            </span>
            {supportDropdownOpen && (
              <div className="dropdown-menu">
                <ul>
                  <li>
                    {/* <button
                      className="dropdown-btn"
                      onClick={() => {
                        navigate("/Catalogue");
                        handleLinkClick();
                      }}
                    >
                      Catalogue
                    </button> */}
                  </li>
                  <li>
                    <button
                      className="dropdown-btn"
                      onClick={() => {
                        navigate("/faqs");
                        handleLinkClick();
                      }}
                    >
                      FAQs
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-btn"
                      onClick={() => {
                        navigate("/contact");
                        handleLinkClick();
                      }}
                    >
                      Contact
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </li>
          <li>
            <span
              onClick={toggleDropdown(
                setTermsDropdownOpen,
                setAboutDropdownOpen,
                setServiceDropdownOpen,
                setSupportDropdownOpen
              )}
            >
              TERMS OF USE
            </span>
            {termsDropdownOpen && (
              <div className="dropdown-menu">
                <ul>
                  <li>
                    <button
                      className="dropdown-btn"
                      onClick={() => {
                        navigate("/terms-conditions");
                        handleLinkClick();
                      }}
                    >
                      Terms & Conditions
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </li>
          <li>
            <Link to="/get-quote" onClick={handleLinkClick}>
              GET QUOTE
            </Link>
          </li>
          <li>
            <Link to="/help" onClick={handleLinkClick}>
              HELP
            </Link>
          </li>
          {/* Login/User Profile moved inside mobile menu */}
          {user ? (
            <li className="nav-item" ref={profileRef}>
              <button
                className="profile-toggle-button"
                onClick={() => setShowProfile((prev) => !prev)}
              >
                <FaUserCircle
                  size={isLaptopMode ? 20 : 22}
                  style={{ marginRight: "6px" }}
                />
                {user.firstName}
              </button>
              {showProfile && (
                <div className="user-profile-details2 space-y-2 p-4 border border-gray-300">
                  {[
                    { label: "First Name", value: user.firstName },
                    { label: "Last Name", value: user.lastName },
                    { label: "Email", value: user.email },
                    { label: "Mobile", value: user.mobile },
                    { label: "Company", value: user.company },
                  ].map(
                    (item, index) =>
                      item.value && (
                        <div key={index} className="flex">
                          <span className="w-[100px] font-semibold">
                            {item.label}
                          </span>
                          <span className="w-4 text-right">:</span>
                          <span className="flex-1 pl-2">{item.value}</span>
                        </div>
                      )
                  )}
                  <div className="flex justify-around">
                    <button
                      onClick={() => setShowProfile(false)}
                      className="mt-4 px-4 py-0 bg-black text-white rounded hover:bg-black"
                    >
                      Close
                    </button>
                    <button
                      onClick={handleLogout}
                      className="mt-4 px-4 py-0 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </li>
          ) : (
            <li>
              <Link to="/login" onClick={handleLinkClick}>
                Login
              </Link>
            </li>
          )}
        </ul>
      </div>
    </header>
  );
};

export default Navbar;
